import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Pipe, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

const CDN_CONFIG = new InjectionToken('psu.utils.cdn.config');
const CDN_RESOURCE_CONFIG = new InjectionToken('psu.utils.cdn.resource.config');
const CDN_RESPONSIVE_RESOURCE_CONFIG = new InjectionToken('psu.utils.cdn.responsive.resource.config');

/**
 * CdnService provides an API to resolve the full URI for resources stored on our remote CDN.
 */
class CdnService {
    constructor(config, resourceConfig, responsiveResourceConfig) {
        this.config = config;
        this.resourceConfig = resourceConfig;
        this.responsiveResourceConfig = responsiveResourceConfig;
    }
    get cdnUrl() {
        let url = 'https://static.apps.psu.edu';
        if (this.config && this.config.baseUrl) {
            url = this.config.baseUrl;
        }
        if (url && url.lastIndexOf('/') === url.length - 1) {
            url = url.substr(0, url.length - 1);
        }
        return url;
    }
    /**
     * @deprecated
     * Multi-Injection tokens are not guaranteed to be able to be accessed from lazily-loaded contexts in Angular 11+
     * Use CdnService.uri instead.
     */
    get allResources() {
        return (this.resourceConfig || [])
            .filter((c) => !!c)
            .map((c) => c.resources)
            .reduce((acc, cur) => ({ ...acc, ...cur }), {});
    }
    /**
     * @deprecated
     * Multi-Injection tokens are not guaranteed to be able to be accessed from lazily-loaded contexts in Angular 11+
     * Use CdnService.uri instead.
     */
    get allResponsiveResources() {
        return (this.responsiveResourceConfig || [])
            .filter((c) => !!c)
            .map((c) => c.responsiveResources)
            .reduce((acc, cur) => ({ ...acc, ...cur }), {});
    }
    /**
     * Resolve the absolute URI for the mobile asset for the given resource key.
     *
     * @param key the name of the resource registered using CDN_RESPONSIVE_RESOURCE_CONFIG injection token
     * @deprecated
     * Multi-Injection tokens are not guaranteed to be able to be accessed from lazily-loaded contexts in Angular 11+
     * Use CdnService.uri instead.
     */
    mobile(key) {
        return this.responsiveResource(key, 'mobile');
    }
    /**
     * Resolve the absolute URI for the desktop asset for the given resource key.
     *
     * @param key the name of the resource registered using CDN_RESPONSIVE_RESOURCE_CONFIG injection token
     * @deprecated
     * Multi-Injection tokens are not guaranteed to be able to be accessed from lazily-loaded contexts in Angular 11+
     * Use CdnService.uri instead.
     */
    desktop(key) {
        return this.responsiveResource(key, 'desktop');
    }
    /**
     * Resolve the CDN resources for a given resource key.  This will return an object that contains
     * the absolute URI to the given resource.
     *
     * @param key the name of the resource registered using CDN_RESPONSIVE_RESOURCE_CONFIG injection token
     * @deprecated
     * Multi-Injection tokens are not guaranteed to be able to be accessed from lazily-loaded contexts in Angular 11+
     * Use CdnService.uri instead.
     */
    responsive(key) {
        let ret = null;
        if (this.allResponsiveResources[key]) {
            ret = { ...this.allResponsiveResources[key] };
            ret.desktop = this.resource(ret.desktop);
            ret.mobile = this.resource(ret.mobile);
        }
        return ret;
    }
    /**
     * Resolve the absolute URI to a given resource.
     *
     * @param path the name of the resource registered using CDN_RESOURCE_CONFIG, or the relative path to the resource
     */
    uri(path) {
        if (this.allResources[path]) {
            path = this.allResources[path];
        }
        return this.resource(path);
    }
    /**
     * @deprecated
     * Multi-Injection tokens are not guaranteed to be able to be accessed from lazily-loaded contexts in Angular 11+
     * Use CdnService.uri instead.
     */
    responsiveResource(key, mode) {
        let path = key;
        if (this.allResponsiveResources[key]) {
            path = this.allResponsiveResources[key][mode];
        }
        return this.resource(path);
    }
    resource(path) {
        return `${this.cdnUrl}/${path}`;
    }
}
CdnService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnService, deps: [{ token: CDN_CONFIG, optional: true }, { token: CDN_RESOURCE_CONFIG, optional: true }, { token: CDN_RESPONSIVE_RESOURCE_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
CdnService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CDN_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CDN_RESOURCE_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CDN_RESPONSIVE_RESOURCE_CONFIG]
                }] }]; } });

class CdnPipe {
    constructor(cdnService) {
        this.cdnService = cdnService;
    }
    transform(value) {
        return !!value ? this.cdnService.uri(value) : '';
    }
}
CdnPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnPipe, deps: [{ token: CdnService }], target: i0.ɵɵFactoryTarget.Pipe });
CdnPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdnPipe, name: "psuCdn" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'psuCdn',
                }]
        }], ctorParameters: function () { return [{ type: CdnService }]; } });

class CdnModule {
}
CdnModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CdnModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: CdnModule, declarations: [CdnPipe], imports: [CommonModule], exports: [CdnPipe] });
CdnModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CdnModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdnPipe],
                    exports: [CdnPipe],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CDN_CONFIG, CDN_RESOURCE_CONFIG, CDN_RESPONSIVE_RESOURCE_CONFIG, CdnModule, CdnPipe, CdnService };
